// Человекочитаемое представление уведомления сотрудника по kind + payload.
// payload может прийти с camelCase (наши писатели) или snake_case (worker
// client_task_reminder) — читаем оба варианта через pick().
//
// Список видов, переходы и подписи — единый каталог `EMPLOYEE_NOTIFICATION_KINDS`
// в @app/domain (план 155 Ф3): его же читают push-крон, Telegram и настройки.
// Здесь — только разметка колокольчика (заголовок и подзаголовок с ФИО
// клиента); что переход совпадает с каталогом, стережёт тест.
import {
  CASH_SHIFT_AUTOCLOSE_NEGATIVE,
  CASH_SHEET_CLOSE_BLOCKED,
  CASH_SHEET_CLOSE_EXCEPTION,
  CASH_SHEET_CLOSE_PERMITTED,
  CASH_SHEET_SOURCE_BROKEN,
  MESSENGER_BROADCAST_UNDELIVERED,
  REFUND_APPROVAL_REQUESTED,
  REFUND_APPROVED,
  REFUND_REJECTED,
  employeeNotificationKind,
  notificationPush,
} from '@app/domain';

import { formatRub } from '@/lib/format';

import type { EmployeeNotification } from '../api/notifications.api';

function pick(payload: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const k of keys) {
    const v = payload[k];
    if (v !== undefined && v !== null) return String(v);
  }
  return undefined;
}

export interface NotificationDisplay {
  title: string;
  subtitle: string | null;
  /** Клиент для перехода в карточку (если применимо). */
  clientId: string | null;
  /** Явный маршрут перехода (приоритетнее clientId). null — по умолчанию. */
  href: string | null;
}

export function describeNotification(n: EmployeeNotification): NotificationDisplay {
  const p = n.payload ?? {};
  const clientId = pick(p, 'clientId', 'client_id') ?? null;

  switch (n.kind) {
    case 'enrollment': {
      const waitlist = p['isWaitlist'] === true || p['is_waitlist'] === true;
      // §49: запись без абонемента — админу видно сразу (ожидается оплата на
      // месте). Старые payload без поля hasSubscription пометку не получают.
      const noSub = p['hasSubscription'] === false || p['has_subscription'] === false;
      // Обогащённый payload (N2): ФИО клиента + название группы. Фолбэк — общая фраза.
      const clientName = pick(p, 'clientName', 'client_name');
      const groupName = pick(p, 'groupName', 'group_name');
      const base =
        clientName || groupName
          ? [clientName, groupName].filter(Boolean).join(' · ')
          : 'Клиент записан на занятие';
      const subtitle = noSub ? `${base} · без абонемента` : base;
      return {
        title: waitlist ? 'Клиент в листе ожидания' : 'Новая запись на занятие',
        subtitle,
        clientId,
        href: null,
      };
    }
    // Батч №10: отмена записи — уведомление администраторам филиала.
    case 'enrollment_cancelled': {
      const clientName = pick(p, 'clientName', 'client_name');
      const groupName = pick(p, 'groupName', 'group_name');
      return {
        title: 'Запись отменена',
        subtitle:
          clientName || groupName
            ? [clientName, groupName].filter(Boolean).join(' · ')
            : 'Клиент отменён с занятия',
        clientId,
        href: null,
      };
    }
    case 'task_assigned': {
      const taskId = pick(p, 'taskId', 'task_id');
      return {
        title: 'Назначена задача',
        subtitle: pick(p, 'title') ?? null,
        clientId,
        href: taskId ? `/tasks?task=${taskId}` : '/tasks',
      };
    }
    case 'shift_tasks_digest': {
      const count = pick(p, 'count') ?? '0';
      return {
        title: 'Задачи на смену',
        subtitle: `Задач на сегодня: ${count}`,
        clientId,
        href: '/tasks',
      };
    }
    // Батч №43 (F2/F3): заявка на привязку мессенджера по вручную введённому
    // номеру — ведёт во вкладку «Привязки мессенджера», не в карточку клиента.
    case 'miniapp.link_request': {
      const who = pick(p, 'requesterName', 'requester_name');
      const phone = pick(p, 'enteredPhone', 'entered_phone');
      return {
        title: 'Запрос на привязку мессенджера',
        subtitle: [who, phone].filter(Boolean).join(' · ') || 'Требуется подтверждение',
        clientId,
        href: '/notifications?tab=links',
      };
    }
    // Клиент просит сменить номер из мини-аппа (заявка ждёт подтверждения
    // администратором). Раньше кейса не было, и в колокольчике светился сырой
    // код `client.phone_change_request` — правка владельца 08.08.2026.
    case 'client.phone_change_request': {
      const phone = pick(p, 'requestedPhone', 'requested_phone');
      const target = pick(p, 'target');
      const which = target === 'secondary' ? 'второй номер' : 'основной номер';
      return {
        title: 'Заявка на смену номера',
        subtitle: [phone, which].filter(Boolean).join(' · ') || 'Требуется подтверждение',
        clientId,
        href: '/notifications?tab=phone-change',
      };
    }
    case 'client_task_reminder': {
      const taskId = pick(p, 'taskId', 'task_id');
      return {
        title: 'Напоминание по задаче',
        subtitle: pick(p, 'title') ?? null,
        clientId,
        href: taskId ? `/tasks?task=${taskId}` : '/tasks',
      };
    }
    case 'automation': {
      // Робот раздела «Автоматизация» (план 73). Без этой ветки сотрудник
      // видел в колокольчике «Уведомление» и слово `automation`, тогда как
      // текст правила лежал в payload (батч №23 П14).
      const dealId = pick(p, 'dealId', 'deal_id');
      const ruleName = pick(p, 'ruleName', 'rule_name');
      return {
        title: ruleName ? `Автоматизация: ${ruleName}` : 'Автоматизация',
        subtitle: pick(p, 'text') ?? null,
        clientId,
        href: dealId ? `/deals?deal=${dealId}` : clientId ? `/clients?client=${clientId}` : null,
      };
    }
    // План 150 Ф5: правка через ассистента владельца — уведомление о КАЖДОЙ
    // (решение владельца 7). Ведёт в «Правки и отмены», где правка помечена.
    case 'assistant_correction': {
      const clientName = pick(p, 'clientName', 'client_name');
      const clientNo = pick(p, 'clientNo', 'client_no');
      const headline = pick(p, 'headline');
      const who = clientName ? `${clientName}${clientNo ? ` (№${clientNo})` : ''}` : null;
      return {
        title: 'Правка через ассистента',
        subtitle: [headline, who].filter(Boolean).join(' · ') || 'Исправление операции',
        clientId,
        href: '/cash/corrections',
      };
    }
    // План 150 Ф6: сотрудник сообщил об ошибке оплаты — заявка ушла
    // управляющему, владелец узнаёт о каждой (решение владельца 8). Ведёт в
    // задачу: оттуда «Исправить».
    case 'correction_request': {
      const taskId = pick(p, 'taskId', 'task_id');
      const clientName = pick(p, 'clientName', 'client_name');
      const clientNo = pick(p, 'clientNo', 'client_no');
      const who = clientName ? `${clientName}${clientNo ? ` (№${clientNo})` : ''}` : null;
      const reporter = pick(p, 'reporterName', 'reporter_name');
      return {
        title: 'Заявка на исправление',
        subtitle:
          [pick(p, 'title'), who, reporter ? `сообщил(а) ${reporter}` : null]
            .filter(Boolean)
            .join(' · ') || 'Ошибка оплаты',
        clientId,
        href: taskId ? `/tasks?task=${taskId}` : '/tasks',
      };
    }
    // План 155 Ф3: к учётке привязали Telegram — на случай, если ссылку
    // привязки переслали чужому, человек увидит это в колокольчике.
    case 'staff_telegram.linked': {
      const username = pick(p, 'username');
      return {
        title: 'Telegram привязан',
        subtitle: username
          ? `@${username} привязан к вашей учётке. Если это не вы — отвяжите в «Моём профиле»`
          : 'Если это не вы — отвяжите в «Моём профиле»',
        clientId,
        href: null,
      };
    }
    // План 155 Ф2: сверка смены с таблицей учёта — «не закрывается»,
    // «разрешено», «закрыта с расхождением / без сверки», «сверка не
    // работает». Тексты и переход (на смену / «Таблицы учёта») — из каталога:
    // в них только филиал, дата и числа, клиентов нет.
    case CASH_SHEET_CLOSE_BLOCKED:
    case CASH_SHEET_CLOSE_PERMITTED:
    case CASH_SHEET_CLOSE_EXCEPTION:
    case CASH_SHEET_SOURCE_BROKEN: {
      const push = notificationPush(n.kind, p);
      return {
        title: push.title,
        subtitle:
          n.kind === CASH_SHEET_CLOSE_BLOCKED
            ? `${push.body}. Разрешить закрытие — в кассе, в блоке сверки`
            : push.body || null,
        clientId: null,
        href: employeeNotificationKind(n.kind)?.href(p) ?? null,
      };
    }
    // План 154 Ф3 (ревью S2): робот не закрыл смену — по расчёту в ящике
    // минус. Текст и переход на смену — из каталога (филиал, дата, сумма).
    case CASH_SHIFT_AUTOCLOSE_NEGATIVE: {
      const push = notificationPush(n.kind, p);
      return {
        title: push.title,
        subtitle: push.body || null,
        clientId: null,
        href: employeeNotificationKind(n.kind)?.href(p) ?? null,
      };
    }
    // План 163 Ф3: рассылка дошла не всем. Числа и причины — из каталога
    // (те же, что в Telegram); здесь к ним — время и начало текста рассылки,
    // чтобы понять, о какой речь. Переход — в журнал рассылок.
    case MESSENGER_BROADCAST_UNDELIVERED: {
      const push = notificationPush(n.kind, p);
      const when = pick(p, 'sentLabel', 'sent_label');
      const preview = pick(p, 'preview');
      const which = [when, preview ? `«${preview}»` : null].filter(Boolean).join(' ');
      return {
        title: push.title,
        subtitle: which ? `${which}: ${push.body}` : push.body,
        clientId: null,
        href: employeeNotificationKind(n.kind)?.href(p) ?? null,
      };
    }
    // План 168: очередь возвратов сверх лимита. Заявка ведёт в очередь (переход
    // из каталога), подтверждение и отказ — в карточку клиента: там возврат,
    // и видно, кому выдавать. Тексты — те же, что в push каталога, плюс номер
    // карточки, как у заявки на исправление.
    case REFUND_APPROVAL_REQUESTED:
    case REFUND_APPROVED:
    case REFUND_REJECTED: {
      const kopecks = Number(pick(p, 'amountKopecks', 'amount_kopecks'));
      const amount = Number.isFinite(kopecks) && kopecks > 0 ? formatRub(kopecks) : null;
      const clientName = pick(p, 'clientName', 'client_name');
      const clientNo = pick(p, 'clientNo', 'client_no');
      const who = clientName ? `${clientName}${clientNo ? ` (№${clientNo})` : ''}` : null;
      const branch = pick(p, 'branchName', 'branch_name');
      const toDeposit = pick(p, 'method') === 'deposit';
      if (n.kind === REFUND_APPROVAL_REQUESTED) {
        return {
          title: 'Возврат ждёт подтверждения',
          subtitle:
            [amount, who, branch].filter(Boolean).join(' · ') || 'Подтвердите или отклоните заявку',
          clientId,
          href: employeeNotificationKind(n.kind)?.href(p) ?? null,
        };
      }
      if (n.kind === REFUND_APPROVED) {
        // Деньги выдают только после подтверждения (решение владельца В4).
        const action = toDeposit
          ? [amount, 'зачислено на депозит'].filter(Boolean).join(' ')
          : `Выдайте ${amount ?? 'деньги'}`;
        return {
          title: 'Возврат подтверждён',
          subtitle: [action, who, branch].filter(Boolean).join(' · '),
          clientId,
          href: null,
        };
      }
      const reason = pick(p, 'rejectionReason', 'rejection_reason');
      return {
        title: 'Возврат отклонён',
        subtitle:
          [amount, who, reason ? `причина: ${reason}` : null].filter(Boolean).join(' · ') ||
          'Деньги не выдавайте',
        clientId,
        href: null,
      };
    }
    default: {
      // Вид из каталога без своей ветки здесь — подпись каталога, а не код.
      const known = employeeNotificationKind(n.kind);
      if (known) {
        return { title: known.label, subtitle: null, clientId, href: known.href(p) };
      }
      // Незнакомый тип: показываем нейтральный заголовок, а сам код — мелким
      // подзаголовком. Раньше сырой `kind` стоял ЗАГОЛОВКОМ, и сотрудник видел
      // в колокольчике «client.phone_change_request». Тест ниже (в
      // notification-display.test.ts) стережёт, чтобы реально пишущиеся типы
      // сюда не попадали.
      return { title: 'Уведомление', subtitle: n.kind, clientId, href: null };
    }
  }
}
